// NGAP Cotation — Adaptateur texte libre / périodes
// Délègue toute la logique de règles au moteur unifié (src/lib/ngap/engine.ts).
// Ce fichier ne fait que : détecter les actes depuis le texte/les périodes,
// appeler le moteur unique, puis reformater le résultat au format historique.

import type {
  ActeDetecte,
  ResultatUnifie,
  PeriodeKey,
  ForceFlags,
} from '@/lib/ngap/types';
import { executerCotation } from '@/lib/ngap/engine';
import { NOMENCLATURE, detecterActe } from '@/lib/ngap/nomenclature';

export interface SoinIdentifie {
  id: string;
  code: string;
  label: string;
  coefficient: number;
  base: string;
  montant: number;
  passage: string;
  categorie: string;
  justification: string;
  article: string;
  tauxFacturation: number; // 100, 50, or 0
}

export interface CumulVerification {
  acte1: string;
  acte2: string;
  compatible: boolean;
  regle: string;
  article: string;
}

export interface MajorationResult {
  code: string;
  nom: string;
  applicable: boolean;
  montant: string;
  raison: string;
  article: string;
}

export interface LimiteVerification {
  code: string;
  nom: string;
  limite: string;
  actuel: string;
  ok: boolean;
  article: string;
}

export interface Contradiction {
  id: string;
  type: 'error' | 'warning' | 'info';
  titre: string;
  description: string;
  suggestion: string;
  severite: 'critique' | 'eleve' | 'modere' | 'faible';
  articles: string[];
}

export interface AnalyseResult {
  cotations: SoinIdentifie[];
  cumuls: CumulVerification[];
  majorations: MajorationResult[];
  limites: LimiteVerification[];
  total: {
    sousTotalActes: string;
    totalMajorations: string;
    totalGeneral: string;
  };
  risque: {
    niveau: string;
    score: number;
    couleur: string;
    details: string[];
  };
  sources: string[];
  avertissements: string[];
  resumeAnalyse: string;
  actesInjection: number;
  actesPansement: number;
  contradictions: Contradiction[];
}

export interface AnalyseInput {
  description: string;
  periods?: {
    matin: string[];
    midi: string[];
    soir: string[];
  };
  forceFlags?: ForceFlags;
}

function formatMontant(amount: number): string {
  return amount.toFixed(2).replace('.', ',') + ' €';
}

interface PassageInfo {
  matin: boolean;
  midi: boolean;
  soir: boolean;
}

function detectPassages(description: string): PassageInfo {
  const lower = description.toLowerCase();
  const matin = /\bmatin\b|8\s*h/i.test(lower);
  const midi = /\bmidi\b|12\s*h/i.test(lower);
  const soir = /\bsoir\b|19\s*h|20\s*h|18\s*h/i.test(lower);

  const deuxFois = /\b2\s*fois\b|\bdeux\s*fois\b|\b2x\b|\bbiquotidien/i.test(lower);
  const troisFois = /\b3\s*fois\b|\btrois\s*fois\b|\b3x\b/i.test(lower);

  if (troisFois) return { matin: true, midi: true, soir: true };
  if (deuxFois && !matin && !midi && !soir) return { matin: true, midi: false, soir: true };

  const hasSpecific = matin || midi || soir;
  return {
    matin: matin || !hasSpecific,
    midi,
    soir,
  };
}

/**
 * Détecte les actes depuis la description texte ou les périodes structurées,
 * et produit la liste d'actes bruts attendue par le moteur unifié.
 */
function detecterActes(input: AnalyseInput): ActeDetecte[] {
  const { description, periods } = input;
  const lower = description.toLowerCase();
  const passages = detectPassages(description);
  const detected: ActeDetecte[] = [];

  const pushAct = (def: NonNullable<ReturnType<typeof detecterActe>>, passage: string, periode: PeriodeKey, sourceKeyword: string) => {
    detected.push({
      id: `${def.code}-${detected.length}`,
      definition: def,
      passage,
      periode,
      sourceKeyword,
    });
  };

  if (periods) {
    const periodMap: Record<string, string> = {
      matin: 'Matin (08h00)',
      midi: 'Midi (12h00)',
      soir: 'Soir (19h00)',
    };
    (Object.keys(periodMap) as Array<'matin' | 'midi' | 'soir'>).forEach((periodKey) => {
      const keywordsList = periods[periodKey];
      if (!keywordsList || keywordsList.length === 0) return;
      keywordsList.forEach((kw) => {
        const def = detecterActe(kw);
        if (!def) return;
        if (def.isDaily) {
          const alreadyAdded = detected.some((d) => d.definition.code === def.code && d.passage === 'Journée');
          if (!alreadyAdded) pushAct(def, 'Journée', 'journee', kw);
          return;
        }
        if (periodKey === 'midi' && def.categorie === 'Pansements') return;
        pushAct(def, periodMap[periodKey], periodKey, kw);
      });
    });
  } else {
    // Mode texte libre
    NOMENCLATURE.forEach((def) => {
      let bestKeywordLen = 0;
      let bestKeyword = '';
      for (const pk of def.keywords) {
        if (lower.includes(pk) && pk.length > bestKeywordLen) {
          bestKeywordLen = pk.length;
          bestKeyword = pk;
        }
      }
      if (bestKeywordLen === 0) return;

      if (def.isDaily) {
        pushAct(def, 'Journée', 'journee', bestKeyword);
        return;
      }
      if (passages.matin) pushAct(def, 'Matin (08h00)', 'matin', bestKeyword);
      if (passages.midi && def.categorie !== 'Pansements') pushAct(def, 'Midi (12h00)', 'midi', bestKeyword);
      if (passages.soir) pushAct(def, 'Soir (19h00)', 'soir', bestKeyword);
    });
  }

  return detected;
}

/**
 * Analyse de cotation — point d'entrée historique.
 * Détecte les actes puis délègue au moteur de règles unifié.
 */
export function analyzeCotation(input: AnalyseInput): AnalyseResult {
  const detected = detecterActes(input);
  const resultat: ResultatUnifie = executerCotation(
    detected,
    input.forceFlags,
    input.description,
  );

  const cotations: SoinIdentifie[] = resultat.actes.map((a) => ({
    id: a.id,
    code: a.code,
    label: a.label,
    coefficient: a.coefficient,
    base: a.base,
    montant: a.montant,
    passage: a.passage,
    categorie: a.categorie,
    justification: a.justification,
    article: a.article,
    tauxFacturation: a.tauxFacturation,
  }));

  const majorations: MajorationResult[] = resultat.majorations.map((m) => ({
    code: m.code,
    nom: m.nom,
    applicable: m.applicable,
    montant: formatMontant(m.montant),
    raison: m.raison,
    article: m.article,
  }));

  const limites: LimiteVerification[] = resultat.limites.map((l) => ({
    code: l.code,
    nom: l.nom,
    limite: l.limite,
    actuel: l.actuel,
    ok: l.ok,
    article: l.article,
  }));

  const cumuls: CumulVerification[] = resultat.cumuls.map((c) => ({
    acte1: c.acte1,
    acte2: c.acte2,
    compatible: c.compatible,
    regle: c.regle,
    article: c.article,
  }));

  const contradictions: Contradiction[] = resultat.contradictions.map((c) => ({
    id: c.id,
    type: c.type,
    titre: c.titre,
    description: c.description,
    suggestion: c.suggestion,
    severite: c.severite,
    articles: c.articles,
  }));

  return {
    cotations,
    cumuls,
    majorations,
    limites,
    total: {
      sousTotalActes: formatMontant(resultat.total.sousTotalActes),
      totalMajorations: `+ ${formatMontant(resultat.total.totalMajorations)}`,
      totalGeneral: formatMontant(resultat.total.totalGeneral),
    },
    risque: resultat.risque,
    sources: resultat.sources,
    avertissements: resultat.avertissements,
    resumeAnalyse: resultat.resumeAnalyse,
    actesInjection: resultat.actesInjection,
    actesPansement: resultat.actesPansement,
    contradictions,
  };
}